// Dialog primitive — thin wrapper around Modal that exposes Dialog sub-components
// matching the shadcn/ui Dialog API so existing imports work without rewriting JSX.
//
// Usage (shadcn pattern):
//   import { Dialog, DialogTrigger, DialogContent, DialogClose, DialogHeader, DialogFooter, DialogTitle, DialogDescription }
//
// Internally reuses Modal (Radix Dialog.Root) and adds the missing sub-components
// (Trigger, Close, Portal, Overlay, Header, Footer, Title, Description).

import React, { forwardRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { motion, focusRing } from './tokens';

// Dialog = Modal (Radix Dialog.Root)
export { default as Dialog } from './Modal';

const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = DialogPrimitive.Overlay;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogTitle = DialogPrimitive.Title;
const DialogDescription = DialogPrimitive.Description;

const DialogContent = forwardRef(function DialogContent(
  { className = '', children, ...props },
  ref,
) {
  return (
    <DialogPortal>
      <DialogOverlay
        className={[
          'fixed inset-0 z-50 bg-black/80',
          motion,
        ]
          .filter(Boolean)
          .join(' ')}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={[
          'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-white p-6 shadow-lg',
          motion,
          focusRing,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        {children}
        <DialogClose
          className={[
            'absolute right-4 top-4 rounded-md opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:pointer-events-none',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

const DialogHeader = forwardRef(function DialogHeader({ className = '', ...props }, ref) {
  return (
    <div
      ref={ref}
      className={['flex flex-col space-y-1.5 text-center sm:text-left', className].filter(Boolean).join(' ')}
      {...props}
    />
  );
});
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = forwardRef(function DialogFooter({ className = '', ...props }, ref) {
  return (
    <div
      ref={ref}
      className={['flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className].filter(Boolean).join(' ')}
      {...props}
    />
  );
});
DialogFooter.displayName = 'DialogFooter';

export {
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
